import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { blogData } from '../data/blogData';
import './NewsDetail.css'; // hero + 2-column layout + sidebar
import './BlogArticle.css'; // structured article block styles

/**
 * Render inline **bold** and *italic* markers inside a string.
 * Splits on the markers and wraps the matched segments in <strong>/<em>.
 */
const renderInline = (text) => {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

/** Render one structured block. See blogData.js for the supported types. */
const Block = ({ block }) => {
  switch (block.type) {
    case 'h2':
      return <h2 className="art-h2">{renderInline(block.text)}</h2>;
    case 'h3':
      return <h3 className="art-h3">{renderInline(block.text)}</h3>;
    case 'p':
      return <p className="art-p">{renderInline(block.text)}</p>;
    case 'list':
      return (
        <ul className="art-list">
          {block.items.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ul>
      );
    case 'formula':
      return <div className="art-formula">{renderInline(block.text)}</div>;
    case 'callout':
      return (
        <div className="art-callout">
          {block.title && <h4 className="art-callout-title">{block.title}</h4>}
          <ul className="art-callout-list">
            {block.items.map((item, i) => (
              <li key={i}>{renderInline(item)}</li>
            ))}
          </ul>
        </div>
      );
    case 'table':
      return (
        <div className="art-table-wrap">
          <table className="art-table">
            <thead>
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i}>{renderInline(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j}>{renderInline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'faq':
      return (
        <div className="art-faq">
          {block.items.map((item, i) => (
            <div key={i} className="art-faq-item">
              <h4 className="art-faq-q">{renderInline(item.q)}</h4>
              <p className="art-faq-a">{renderInline(item.a)}</p>
            </div>
          ))}
        </div>
      );
    case 'disclaimer':
      return <p className="art-disclaimer">{renderInline(block.text)}</p>;
    case 'references':
      return (
        <ol className="art-references">
          {block.items.map((item, i) => (
            <li key={i}>{renderInline(item)}</li>
          ))}
        </ol>
      );
    default:
      return null;
  }
};

const BlogDetail = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);

  useEffect(() => {
    const found = blogData.find((item) => item.id === parseInt(id, 10));
    setPost(found);
    window.scrollTo(0, 0);
  }, [id]);

  if (!post) return <div>Chargement...</div>;

  return (
    <>
      <SEO
        title={`${post.title} | Blog ATAA REALTY`}
        description={post.excerpt || post.subtitle}
        canonical={`https://ataarealty.com/blog/${post.id}`}
        ogType="article"
        ogImage={post.image}
        keywords={`${post.category}, immobilier Maroc, ATAA Realty`}
      />
      <Navbar />

      <div
        className="article-header"
        style={{ backgroundImage: `url(${post.image})` }}
      >
        <div className="article-overlay"></div>
        <div className="container header-text">
          <span className="article-meta">
            {post.category} — {post.date}
          </span>
          <h1>{post.title}</h1>
        </div>
      </div>

      <div className="container article-container art-layout">
        <div className="article-content">
          <div className="main-text art-body">
            {post.subtitle && (
              <p className="art-subtitle">{renderInline(post.subtitle)}</p>
            )}
            {post.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
        </div>

        <div className="article-sidebar">
          <h3>Articles Récents</h3>
          <ul>
            {blogData.map((b) => (
              <li key={b.id}>
                <Link to={`/blog/${b.id}`}>{b.title}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default BlogDetail;
